// src/components/T4RLJDAgentAppBar.js
import { PRODUCT_NAME } from "../config/product";
import React from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  Tooltip,
  Button,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import {
  Drawer,
  List,
  ListItem,
  Divider,
  IconButton
} from "@mui/material";

// Reuse AI color scheme from AIBorderWrapper
const STATIC_GRADIENT =
  "linear-gradient(135deg, #ff6ec4 0%, #6366f1 25%, #22c55e 50%, #fbbf24 75%, #ff6ec4 100%)";

// Gradient title text, with optional static gradient
// and fallback to normal bold white text when not highlighted
const GradientTitle = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "highlight",
})(({ theme, highlight }) => {
  if (!highlight) {
    // Normal, modern MUI heading when not in LLM processing
    return {
      fontWeight: 700,
      fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
      letterSpacing: 0.3,
      color: theme.palette.common.white,
    };
  }

  // Static rainbow gradient during LLM processing (step 5 / 7)
  return {
    fontWeight: 700,
    fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
    letterSpacing: 0.3,
    backgroundImage: STATIC_GRADIENT,
    backgroundSize: "100% 100%",
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    color: "transparent",
    WebkitTextFillColor: "transparent",
  };
});

// Big icon that fills most of the AppBar height
const BigPsychologyIcon = styled(PsychologyOutlinedIcon)(({ theme }) => ({
  color: theme.palette.common.white,
  fontSize: 64, // ~80px toolbar height -> ~90% of the vertical space
}));

/**
 * T4RAppBar
 *
 * Props:
 *  - isLlmProcessing: boolean  // true when LLMProcessingCard (step 5 or 7) is shown
 *  - onOpenAbout: function     // called when user clicks the About button
 */
export default function T4RAppBar({
  isLlmProcessing = false,
  onOpenAbout,
  extraActions = [], // [{ key, label, icon, onClick }] e.g. How to use, Your session
  statusSlot = null, // e.g. the autosave status chip
  user,
  onLogout,
  textSizeScale = 1.0,
  onTextSizeChange,
}) {
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const forceMobile = textSizeScale >= 1.2;
  const appBarTextScale = Math.min(textSizeScale, 1.1);
  const isLoginRequired = process.env.REACT_APP_LOGIN_NEEDED === "true";
  const shouldShowUser = user && (isLoginRequired || user !== "guest@t4r.local");

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleIncreaseText = () => {
    if (onTextSizeChange) onTextSizeChange((prev) => Math.min(prev + 0.1, 1.5));
  };

  const handleDecreaseText = () => {
    if (onTextSizeChange) onTextSizeChange((prev) => Math.max(prev - 0.1, 0.8));
  };

  const drawerContent = (
    <Box
      sx={{
        width: 250,
        p: 2,
        minHeight: "100vh",
        bgcolor: "primary.main",
        color: "common.white",
        boxSizing: "border-box" // prevent padding expanding width
      }}
      role="presentation"
      onClick={(e) => {
        // Only toggle drawer if they click a List item or the Drawer itself,
        // but not when clicking empty space (though Drawer backdrop handles that).
        // For our purpose, keeping the onClick here handles mostly links/buttons closing the drawer.
      }}
    >
      {/* Close button icon at the top right */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <IconButton
          onClick={handleDrawerToggle}
          sx={{ color: 'common.white' }}
          aria-label="close drawer"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Logos inside Drawer */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}>
        <Box
          component="a"
          href="https://k8.design/"
          target="_blank"
          rel="noopener noreferrer"
          sx={{ display: "inline-flex", alignItems: "center" }}
        >
          <Box
            component="img"
            src={`${process.env.PUBLIC_URL}/K8-LOGO-SCHRIFT-white-cutout.png`} // Use white logos
            alt="K8 Institut"
            sx={{ height: 32, width: "auto", objectFit: "contain" }}
          />
        </Box>
        <Box
          component="a"
          href="https://t4r.nweurope.eu/"
          target="_blank"
          rel="noopener noreferrer"
          sx={{ display: "inline-flex", alignItems: "center" }}
        >
          <Box
            component="img"
            src={`${process.env.PUBLIC_URL}/T4R_Logo-01_trans.png`}
            alt="Twin4Resilience"
            sx={{ height: 32, width: "auto", objectFit: "contain" }}
          />
        </Box>
      </Box>

      <Divider sx={{ mb: 2, borderColor: "rgba(255,255,255,0.2)" }} />

      <List sx={{ width: "100%", boxSizing: "border-box", overflowX: "hidden" }}>
        {shouldShowUser && (
          <ListItem disablePadding sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: "common.white", width: "100%" }}>
              <AccountCircleOutlinedIcon sx={{ flexShrink: 0 }} />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  wordBreak: "break-all",
                  whiteSpace: "normal"
                }}
              >
                {user}
              </Typography>
            </Box>
          </ListItem>
        )}

        <ListItem disablePadding sx={{ mb: 1.5, width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", px: 1 }}>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }}>Text size</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleDecreaseText}
              disabled={textSizeScale <= 0.8}
              sx={{ minWidth: 40, p: 0.5, borderColor: "rgba(255,255,255,0.5)", color: "common.white" }}
            >
              A-
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleIncreaseText}
              disabled={textSizeScale >= 1.5}
              sx={{ minWidth: 40, p: 0.5, borderColor: "rgba(255,255,255,0.5)", color: "common.white" }}
            >
              A+
            </Button>
          </Box>
        </ListItem>

        {statusSlot && (
          <ListItem disablePadding sx={{ mb: 1, width: "100%" }}>{statusSlot}</ListItem>
        )}
        {extraActions.map((a) => (
          <ListItem key={a.key} disablePadding sx={{ mb: 1, width: "100%" }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={a.onClick}
              startIcon={a.icon}
              sx={{
                justifyContent: "flex-start",
                textTransform: "none",
                color: "common.white",
                borderColor: "rgba(255,255,255,0.5)",
                "&:hover": { borderColor: "common.white", bgcolor: "rgba(255,255,255,0.1)" }
              }}
            >
              {a.label}
            </Button>
          </ListItem>
        ))}

        <ListItem disablePadding sx={{ mb: 1, width: "100%" }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={onOpenAbout}
            startIcon={<InfoOutlinedIcon />}
            sx={{
              justifyContent: "flex-start",
              textTransform: "none",
              color: "common.white",
              borderColor: "rgba(255,255,255,0.5)",
              "&:hover": { borderColor: "common.white", bgcolor: "rgba(255,255,255,0.1)" }
            }}
          >
            About
          </Button>
        </ListItem>

        {onLogout && (
          <ListItem disablePadding sx={{ width: "100%" }}>
            <Button
              fullWidth
              variant="outlined"
              color="error"
              onClick={onLogout}
              startIcon={<LogoutOutlinedIcon />}
              sx={{
                justifyContent: "flex-start",
                textTransform: "none",
                color: "error.light",
                borderColor: "error.main",
                "&:hover": { borderColor: "error.light", bgcolor: "rgba(211,47,47,0.1)" }
              }}
            >
              Logout
            </Button>
          </ListItem>
        )}
      </List>
    </Box>
  );

  return (
    <AppBar position="sticky" elevation={0} color="primary">
      <Toolbar
        sx={{
          py: 1,
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "nowrap"
        }}
      >
        {/* === MOBILE ONLY: Hamburger Menu === */}
        <Box sx={{ display: forceMobile ? "flex" : { xs: "flex", md: "none" } }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
          >
            <MenuIcon />
          </IconButton>
        </Box>

        {/* === DESKTOP ONLY: LEFT Logos === */}
        <Box
          sx={{
            display: forceMobile ? "none" : { xs: "none", md: "flex" },
            alignItems: "center",
            gap: 1.5,
            flexShrink: 0,
            mr: 2
          }}
        >
          {/* K8 logo -> https://k8.design/ */}
          <Box
            component="a"
            href="https://k8.design/"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: "inline-flex",
              alignItems: "center",
            }}
          >
            <Box
              component="img"
              src={`${process.env.PUBLIC_URL}/K8-LOGO-SCHRIFT-white-cutout.png`}
              alt="K8 Institut für strategische Ästhetik"
              sx={{
                height: 32,
                width: "auto",
                objectFit: "contain",
              }}
            />
          </Box>

          {/* T4R logo -> https://t4r.nweurope.eu/ */}
          <Box
            component="a"
            href="https://t4r.nweurope.eu/"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: "inline-flex",
              alignItems: "center",
            }}
          >
            <Box
              component="img"
              src={`${process.env.PUBLIC_URL}/T4R_Logo-01_trans.png`}
              alt="Twin4Resilience"
              sx={{
                height: { xs: 24, sm: 32 },
                width: "auto",
                objectFit: "contain",
              }}
            />
          </Box>
        </Box>

        {/* CENTER: title (always visible) + psychology icon */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 1, md: 2 },
            justifyContent: "center",
            flexGrow: { xs: 1, md: 0 },
            mx: { md: "auto" }
          }}
        >
          {/* Psychology icon (hide on very small mobile if needed, but try to keep it) */}
          <Box sx={{ display: { xs: "none", sm: "block" } }}>
            <BigPsychologyIcon />
          </Box>

          {/* Title + Beta label */}
          <Box sx={{ textAlign: { xs: "left", md: "right" } }}>
            <GradientTitle
              variant="h5" // larger, modern headline
              highlight={isLlmProcessing}
              sx={{
                lineHeight: 1.2,
                fontSize: {
                  xs: `${16 * appBarTextScale}px`,
                  sm: `${20.8 * appBarTextScale}px`,
                  md: `${24 * appBarTextScale}px`
                },
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis"
              }}
            >
              {PRODUCT_NAME}
            </GradientTitle>
          </Box>

          {/* App logo immediately to the right of the title block */}
          <Box
            component="img"
            src={`${process.env.PUBLIC_URL}/t4r-ljd.png`}
            alt="Learning Journey Designer App Logo"
            sx={{
              width: { xs: 64, sm: 96, md: 128 },
              height: { xs: 32, sm: 48, md: 64 },
              borderRadius: "4px",
              cursor: "default",
              objectFit: "contain",
              display: { xs: 'none', sm: 'block' }
            }}
          />
        </Box>

        {/* === DESKTOP ONLY: RIGHT User info + Logout + About === */}
        <Box
          sx={{
            display: forceMobile ? "none" : { xs: "none", md: "flex" },
            alignItems: "center",
            gap: 2,
            flexShrink: 0
          }}
        >
          {shouldShowUser && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, opacity: 0.9 }}>
              <AccountCircleOutlinedIcon fontSize="small" />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {user}
              </Typography>
            </Box>
          )}

          {/* Text Size Accessibility Controls (Desktop) */}
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mr: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleDecreaseText}
              disabled={textSizeScale <= 0.8}
              sx={{
                minWidth: 36,
                padding: "2px 0",
                borderColor: "rgba(255,255,255,0.5)",
                color: "rgba(255,255,255,0.9)",
              }}
              title="Decrease text size"
            >
              A-
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleIncreaseText}
              disabled={textSizeScale >= 1.5}
              sx={{
                minWidth: 36,
                padding: "2px 0",
                borderColor: "rgba(255,255,255,0.5)",
                color: "rgba(255,255,255,0.9)",
              }}
              title="Increase text size"
            >
              A+
            </Button>
          </Box>

          {statusSlot}

          <Box sx={{ display: 'flex', gap: 1 }}>
            {extraActions.map((a) => (
              <React.Fragment key={a.key}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={a.onClick}
                  startIcon={a.icon}
                  sx={{
                    display: { xs: "none", xl: "inline-flex" },
                    borderColor: "rgba(255,255,255,0.7)",
                    color: "rgba(255,255,255,0.9)",
                    textTransform: "none",
                    "&:hover": { borderColor: "rgba(255,255,255,0.9)", backgroundColor: "rgba(255,255,255,0.08)" },
                  }}
                >
                  {a.label}
                </Button>
                <Tooltip title={a.label}>
                  <IconButton
                    aria-label={a.label}
                    onClick={a.onClick}
                    size="small"
                    sx={{ display: { xs: "inline-flex", xl: "none" }, color: "rgba(255,255,255,0.9)",
                      border: "1px solid rgba(255,255,255,0.5)", borderRadius: 1 }}
                  >
                    {a.icon}
                  </IconButton>
                </Tooltip>
              </React.Fragment>
            ))}
            {onLogout && (
              <Button
                variant="outlined"
                size="small"
                onClick={onLogout}
                startIcon={<LogoutOutlinedIcon />}
                sx={{
                  borderColor: "rgba(255,255,255,0.7)",
                  color: "rgba(255,255,255,0.9)",
                  textTransform: "none",
                  "&:hover": {
                    borderColor: "rgba(255,255,255,0.9)",
                    backgroundColor: "rgba(255,255,255,0.08)",
                  },
                }}
              >
                Logout
              </Button>
            )}

            <Button
              variant="outlined"
              size="small"
              onClick={onOpenAbout}
              startIcon={<InfoOutlinedIcon />}
              sx={{
                borderColor: "rgba(255,255,255,0.7)",
                color: "rgba(255,255,255,0.9)",
                textTransform: "none",
                "&:hover": {
                  borderColor: "rgba(255,255,255,0.9)",
                  backgroundColor: "rgba(255,255,255,0.08)",
                },
              }}
            >
              About
            </Button>
          </Box>
        </Box>
      </Toolbar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: forceMobile ? "block" : { xs: "block", md: "none" },
          "& .MuiDrawer-paper": { boxSizing: "border-box", width: 250 },
        }}
      >
        {drawerContent}
      </Drawer>
    </AppBar>
  );
}
